import { createEffect } from "solid-js";
import { useLocation } from "@solidjs/router";
import { sessionState } from "@/shared/stores/sessionStore";
import { themeState } from "@/shared/stores/themeStore";
import { setContext, trackPageView } from "./analytics";
import type { AuthState, BrandMode, UserGroup } from "./types";

/**
 * Measures route changes, and keeps the event envelope in step with who is looking and which brand
 * they are looking at.
 *
 * <p>The router changes the URL without a document load, so nothing else states which page the
 * visitor is on after the first one. What this must not do is state it twice: the tracking layer
 * sends one page_view of its own the moment the container comes up — which for a returning visitor
 * is during start-up, on the very route this hook is watching — and a second one from here would
 * double every session's landing page.</p>
 */

/**
 * Build identity for the envelope. A build-time variable, like every other <c>VITE_*</c>: it is
 * baked in by <c>npm run build</c> and set as a pipeline variable — declared in
 * <c>src/vite-env.d.ts</c>, so it is read as itself rather than through a cast.
 *
 * <p>Blank counts as absent, not as a version. A pipeline that declares the variable without giving
 * it a value — which is exactly what <c>.env.example</c> ships — bakes in an empty string, and an
 * empty <c>app_version</c> is a column a report cannot group two deployments by while looking like
 * it can. "unknown" at least says so.</p>
 */
const APP_VERSION = import.meta.env.VITE_APP_VERSION?.trim() || "unknown";

/**
 * The last route a page_view was considered for. Module-level rather than per-hook-instance, and
 * that placement is the whole mechanism: on the first run there is nothing to compare against, so
 * nothing is sent (the document load is not a route change); on a remount — a hot reload, a layout
 * that re-renders — the value is already the current route, so nothing is sent then either. Only an
 * actual navigation produces a value that differs from what is held here.
 */
let lastPath: string | null = null;

// ── Envelope ─────────────────────────────────────────────────────────────────

/**
 * Which of the three groups the visitor belongs to, or null while it is unknown.
 *
 * <p>Null is not "probably a customer". The tracking layer treats an unresolved group as group A and
 * sends nothing, and that is the direction this has to fail in: a session request that failed must
 * not be the thing that puts a sales representative into a remarketing audience.</p>
 *
 * <p>Group A is the internal side — a representative on a <c>?token=</c> deep link, and anyone
 * holding an internal-facing permission. <c>garageExpert</c> is counted with <c>admin</c> and
 * <c>garageInternal</c> here even though the group definition names only the latter two: the session
 * store already treats all three as the internal roles, and someone with internal tooling is staff
 * whichever permission carries it. Over-counting group A costs a few rows of analytics; under-
 * counting it puts staff traffic in the audiences the ad platforms bid on.</p>
 */
function resolveUserGroup(): UserGroup | null {
  if (!sessionState.loaded || sessionState.failed) return null;

  const context = sessionState.context;
  if (context === null) return null;

  if (context.principal === "internal") return "A";

  const permissions = context.permissions ?? [];
  if (
    permissions.includes("admin") ||
    permissions.includes("garageInternal") ||
    permissions.includes("garageExpert")
  ) {
    return "A";
  }

  return context.principal === "dealer" ? "B" : "C";
}

/** Signed in, or not. The user row is what the backend keys on, so it is what is reported. */
function resolveAuthState(): AuthState {
  return sessionState.context?.userId != null ? "authenticated" : "guest";
}

/**
 * Whether this is LOBAS' own site or a partner's. Both the server's default tenant section and the
 * hard-coded fallback answer for LOBAS, under two different ids, so both are named.
 */
function resolveBrandMode(tenantId: string): BrandMode {
  return tenantId === "lobas" || tenantId === "default" ? "lobas" : "whitelabel";
}

/**
 * The dealer company an event belongs to, or null.
 *
 * <p>Not part of the envelope: <c>dealer_id</c> is a per-event parameter, declared only by the
 * events where a dealer is actually the subject. It is exposed here so those call sites resolve it
 * from one place rather than each reaching into the session store, and because it is an ERP account
 * number for a company — never a contact person, which is what keeps it out of the personal data
 * that may not reach GA4.</p>
 */
export function currentDealerId(): string | null {
  return sessionState.context?.dealerId ?? null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Call once, from the app layout, during component setup. Registers two effects: one that mirrors
 * the session and the tenant into the event envelope, and one that measures navigation.
 *
 * <p>The envelope effect matters as much as the page_view one. The tracking layer refuses to load
 * the container at all while the group is unresolved, so this is also what releases it once the
 * session settles — and what keeps it shut for staff.</p>
 */
export function useTracking(): void {
  createEffect(() => {
    setContext({
      userGroup: resolveUserGroup(),
      authState: resolveAuthState(),
      tenantId: themeState.tenant.id,
      brandMode: resolveBrandMode(themeState.tenant.id),
      appVersion: APP_VERSION,
    });
  });

  const location = useLocation();

  createEffect(() => {
    // Both read up front so the effect's dependencies do not change with the branch it takes.
    const path = location.pathname;
    const search = location.search;

    // The path alone decides whether this is a new page. Query strings here are app state — a
    // configurator step, a dashboard filter — and the sales deep link's ?token= is stripped from the
    // URL by the session store a moment after load, which on a query-sensitive key would report the
    // landing page twice. What the visitor navigated between is the path.
    if (path === lastPath) return;

    const isFirstRoute = lastPath === null;
    lastPath = path;

    // The document load is not a route change. The page_view for wherever the visitor came in is
    // sent by the tracking layer when the container comes up, which is the only moment that knows
    // whether it was allowed to come up at all.
    if (isFirstRoute) return;

    // Sent with the query, even though it did not decide anything: sanitizeUrl keeps only the
    // allow-listed parameters, and the campaign ones are among them.
    trackPageView(path + search);
  });
}
